import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { isValidPostalCode } from './locationUtils';
import { getCookie, setCookie } from './cookies';
import {
	getDDCUrlParams,
	getDDCUserLocation,
	getDDCDealershipPostalCode
} from './ddcHelper';
import {
	DEFAULT_POSTAL_CODE,
	POSTAL_CODE_COOKIE_NAME
} from '../global-constants';

const LOG_PREFIX = '[ws-locations-data][defaultPostalCode]';
const COOKIE_LABEL = 'cookie';
const POSTAL_CODE_SOURCES = [
	{ label: 'URL geoZip', get: () => getDDCUrlParams().geoZip },
	{ label: COOKIE_LABEL, get: () => getCookie(POSTAL_CODE_COOKIE_NAME) },
	{ label: 'userLocation', get: () => getDDCUserLocation()?.userPostalCode },
	{ label: 'dealership', get: () => getDDCDealershipPostalCode() }
];

/**
 * Persists the resolved postal code into the DDC.postalCode cookie when
 * the cookie is currently empty. This lets other widgets (and subsequent
 * calls to this util) pick up the value via the cookie source on the next
 * page load.
 */
const persistToCookieIfEmpty = (value, matchedLabel) => {
	if (!value || matchedLabel === COOKIE_LABEL) {
		return;
	}
	const existing = getCookie(POSTAL_CODE_COOKIE_NAME)?.trim();
	if (existing) {
		return;
	}
	setCookie(POSTAL_CODE_COOKIE_NAME, value);
	logger.info(
		`${LOG_PREFIX} Persisted postal code to ${POSTAL_CODE_COOKIE_NAME} cookie: ${value}`
	);
};

/**
 * Returns the best available postal code in priority order:
 * 1. URL param   — DDC.getUrlParams().geoZip
 * 2. Cookie      — DDC.postalCode
 * 3. User        — DDC.userLocation.userPostalCode
 * 4. Dealer zip  — DDC.dataLayer.dealership.postalCode
 * 5. Default     — DEFAULT_POSTAL_CODE (10001)
 *
 * Side effect: when the resolved value comes from a non-cookie source and
 * the DDC.postalCode cookie is empty, this function writes the value back
 * to that cookie so other widgets (and subsequent calls) can pick it up
 * via the cookie source on later reads.
 *
 * Returns null when not running in a browser.
 */
export const getDefaultPostalCode = () => {
	if (!isBrowser) {
		logger.info(`${LOG_PREFIX} Not in browser, skipping`);
		return null;
	}

	const match = POSTAL_CODE_SOURCES.find((source) => {
		const value = source.get()?.trim();
		return value && isValidPostalCode(value);
	});

	if (match) {
		const value = match.get()?.trim();
		logger.info(
			`${LOG_PREFIX} Using postal code from ${match.label}: ${value}`
		);
		persistToCookieIfEmpty(value, match.label);
		return value;
	}

	logger.info(
		`${LOG_PREFIX} No valid postal code found from any source, using default: ${DEFAULT_POSTAL_CODE}`
	);
	persistToCookieIfEmpty(DEFAULT_POSTAL_CODE, null);
	return DEFAULT_POSTAL_CODE;
};
